import { Component, ViewChild } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FileSelectEvent, FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { BehaviorSubject, delay as delayRx, ReplaySubject } from 'rxjs';
import {
  AgreementItem,
  AgreementItemEssential,
  AssetManagementItem,
  AttachmentsItem,
  DdtItemEssential,
  DeliveryItemEssential,
  DuplicationType,
  EntityAttachmentsItem,
  OrderCustomerItemEssential,
  OrderItemEssential,
  ScheduleItem,
  TicketItem,
  WarehouseMovement,
} from 'src/app/api/models';
import { AllegatiService, MediaService } from 'src/app/api/services';
import { UploadQueueItem } from 'src/app/utils/models/upload.model';
import { delay, downloadFile } from 'src/app/utils/utils';
import { CommonService } from 'src/app/services/common.service';

export type AttachmentEntity =
  | AgreementItemEssential
  | AssetManagementItem
  | OrderCustomerItemEssential
  | DdtItemEssential
  | DeliveryItemEssential
  | OrderItemEssential
  | ScheduleItem
  | TicketItem
  | WarehouseMovement;

export type AttachmentType =
  | 'agreement'
  | 'asset_management'
  | 'ddt'
  | 'delivery'
  | 'order'
  | 'order_customer'
  | 'schedule'
  | 'ticket'
  | 'warehouse';

export interface ModalUploadData {
  selectedEntity: AttachmentEntity;
  entityType: AttachmentType;
}

@Component({
  selector: 'app-modal-upload',
  templateUrl: './modal-upload.component.html',
  styleUrls: ['./modal-upload.component.scss'],
})
export class ModalUploadComponent {
  protected readonly rowsPerPageOptions = [25, 50, 100, 200, 500];
  @ViewChild('uploader') uploader?: FileUpload;
  uploadQueue: UploadQueueItem[] = [];
  attachmentList: EntityAttachmentsItem[] = [];
  attachments: AttachmentsItem[] = [];
  selectedEntity!: AttachmentEntity;
  entityType!: AttachmentType;
  uploadCompleted = false;
  uploading = false;
  deleteGrants: string[] = [];
  createGrants: string[] = [];
  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private allegatiService: AllegatiService,
    private mediaService: MediaService,
    private localCommon: CommonService
  ) {}

  ngOnInit() {
    const data: ModalUploadData = this.config.data!;
    this.selectedEntity = data.selectedEntity;
    this.entityType = data.entityType;
    switch (this.entityType) {
      case 'delivery':
        this.createGrants = ['DELIVERIES.U'];
        this.deleteGrants = ['DELIVERIES.D'];
        break;
      case 'order':
        this.createGrants = ['ORDERS_SUPPLIER.U'];
        this.deleteGrants = ['ORDERS_SUPPLIER.D'];
        break;
      case 'order_customer':
        this.createGrants = ['ORDERS_CUSTOMER.U'];
        this.deleteGrants = ['ORDERS_CUSTOMER.D'];
        break;
      case 'ddt':
        this.createGrants = ['DDT.U'];
        this.deleteGrants = ['DDT.D'];
        break;
      case 'agreement':
        this.createGrants = ['AGREEMENTS.U'];
        this.deleteGrants = ['AGREEMENTS.D'];
        break;
      case 'ticket':
        this.createGrants = ['TICKETS.U'];
        this.deleteGrants = ['TICKETS.D'];
        break;
      case 'schedule':
        this.createGrants = ['TICKET-SCHEDULES.U'];
        this.deleteGrants = ['TICKET-SCHEDULES.D'];
        break;
      case 'asset_management':
        this.createGrants = ['ASSET_MANAGEMENT.U'];
        this.deleteGrants = ['ASSET_MANAGEMENT.D'];
        break;
      case 'warehouse':
        this.createGrants = [
          'WAREHOUSE-MOVEMENTS-LOAD.U',
          'WAREHOUSE-MOVEMENTS-RETURN.U',
          'WAREHOUSE-MOVEMENTS-TRANSFER.U',
          'WAREHOUSE-MOVEMENTS-ADJUSTMENT.U',
        ];
        this.deleteGrants = [
          'WAREHOUSE-MOVEMENTS-LOAD.D',
          'WAREHOUSE-MOVEMENTS-RETURN.D',
          'WAREHOUSE-MOVEMENTS-TRANSFER.D',
          'WAREHOUSE-MOVEMENTS-ADJUSTMENT.D',
        ];
        break;
      default:
        this.createGrants = [];
        this.deleteGrants = [];
        break;
    }
    this.loadAttachmentsType();
    this.loadAllegati();
  }

  onSelectedFiles(event: FileSelectEvent) {
    for (let file of event.files) {
      const form_data = new FormData();

      this.uploadQueue.push({
        file,
        attachment_type_id: this.attachments[0].id,
        progress: new BehaviorSubject(-1),
        result: false,
        canBeDeleted: false,
      });
    }
  }

  onUpload(event: FileUploadHandlerEvent) {
    console.log('onUpload', event);
    this.uploading = true;
    this.clearCompleted();
    this.uploadQueue = this.uploadQueue.map(u => ({
      file: u.file,
      progress: new BehaviorSubject(-1),
      result: false,
      canBeDeleted: false,
      attachment_type_id: u.attachment_type_id,
    }));
    this.processUploadingQueue();
  }

  removeFromQueue(event: Event, i: number) {
    this.uploadQueue.splice(i, 1);
    this.uploader?.remove(event, i);
  }

  removeFileCallback() {}
  removeUploadedFileCallback() {}

  clearCompleted() {
    this.uploadQueue = this.uploadQueue.filter((x, i) => {
      if (x.canBeDeleted) {
        this.uploader?.remove(new Event('click'), i);
      }
      return !x.canBeDeleted;
    });
  }

  clearQueue() {
    this.uploadQueue = [];
  }

  close() {
    this.ref.close();
  }

  private async processUploadingQueue() {
    if (this.uploadQueue.length > 0) {
      this.uploadQueue.forEach(u => u.progress.next(0));
      for (let u of this.uploadQueue.filter(u => u.attachment_type_id)) {
        console.log('processUploadingQueue', u);
        try {
          await this.uploadSingleFile(u);
        } catch (error) {
          console.error('Upload failed for file:', u.file.name, error);
        }
        await delay(500);
      }
      this.uploading = false;
      this.clearCompleted();
      this.loadAllegati();
    }
  }

  private loadAttachmentsType() {
    this.localCommon.attachments.subscribe({
      next: attachments => {
        this.attachments = attachments.filter(a => {
          switch (this.entityType) {
            case 'agreement':
              return a.for_agreement;
            case 'asset_management':
              return a.for_asset_management;
            case 'ddt':
              return a.for_ddt;
            case 'delivery':
              return a.for_delivery;
            case 'order':
              return a.for_order;
            case 'order_customer':
              return a.for_order_customer;
            case 'schedule':
              return a.for_schedule;
            case 'ticket':
              return a.for_ticket;
            case 'warehouse':
              return a.for_warehouse;
            default:
              return false;
          }
        });
      },
    });
  }

  private loadAllegati() {
    this.allegatiService
      .attachmentsControllerGetAllAttachments({
        id: this.selectedEntity!.id,
        entity: this.entityType,
      })
      .subscribe({
        next: res => {
          this.attachmentList = res.data.list;
        },
        error: error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: `Si è verificato un errore durante il recupero degli allegati. Impossibile aprire la lista degli allegati.`,
          });
        },
      });
  }

  async uploadSingleFile(u: UploadQueueItem) {
    return new Promise<boolean>((resolve, reject) => {
      const type = this.attachments.find(a => a.id === u.attachment_type_id);
      if (type && u.file.size > type.max_size_mb * 1024 * 1024) {
        // senza complete(): il rifiuto avviene prima che il template si sottoscriva al progresso
        // e un BehaviorSubject completato non ripropone l'ultimo valore ai nuovi iscritti
        u.progress.next(-2);
        u.result = false;
        u.canBeDeleted = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: `Il file "${u.file.name}" pesa ${(u.file.size / 1024 / 1024)
            .toFixed(1)
            .replace('.', ',')} Mb e supera la dimensione massima di ${
            type.max_size_mb
          } Mb consentita per il tipo di allegato "${type.type_description}".`,
        });
        resolve(false);
        return;
      }
      u.progress.next(0);
      this.allegatiService
        .attachmentsControllerAddAttachment({
          entity: this.entityType!,
          id: this.selectedEntity!.id,
          type: u.attachment_type_id!,
          body: {
            allegato: u.file,
          },
        })
        .subscribe({
          next: res => {
            console.log(u, 'uploaded successfully');
            u.progress.next(100);
            u.progress.complete();
            u.result = true;
            u.canBeDeleted = true;
            resolve(true);
          },
          error: async error => {
            console.log(u, 'uploaded failed');
            u.progress.next(-2);
            u.progress.complete();
            u.result = false;
            u.canBeDeleted = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: `Si è verificato un errore durante l'upload di ${u.file.name}. ${
                error.error?.message || error.message
              }`,
            });
            resolve(false);
          },
        });
    });
  }

  confirmDelete(m: EntityAttachmentsItem) {
    this.confirmationService.confirm({
      message: 'Sei sicuro di voler eliminare questo allegato?',
      header: 'Attenzione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì',
      acceptButtonStyleClass: 'bg-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.allegatiService
          .attachmentsControllerDeleteAttachment({
            entity: this.entityType!,
            id: this.selectedEntity!.id,
            type: m!.attachment_type.id,
            media_id: m.media.id,
          })
          .subscribe({
            next: res => {
              this.loadAllegati();
            },
            error: err => {
              this.messageService.add({
                severity: 'error',
                summary: 'Eliminazione',
                detail: err.error.message,
              });
            },
          });
      },
    });
  }
  download({ media }: EntityAttachmentsItem) {
    this.mediaService
      .mediaControllerGetMedia$Response({
        path: media.path,
      })
      .subscribe({
        next: response => {
          downloadFile(response.body, media.original_name);
        },
        error: error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Eliminazione',
            detail: 'Si è verificato un errore durante il download del file.',
          });
        },
      });
  }
}
